/**
 * The fixed lists a ticket is described with (ops insights Phase 5a, Q1). A shop cannot edit
 * them in v1, so they live in code, each with a CHECK in migration 0037 holding a second copy.
 *
 * ⚠️ SQL cannot import this file. Changing a list here needs a migration that rewrites its CHECK
 * (DROP + ADD), or the first write of the new value is refused by the database.
 * `packages/db/test/tickets-migration.test.ts` reads every value here through the CHECKs, so the
 * two cannot disagree unnoticed.
 *
 * The array order is the order a picker shows.
 */

/** Where a ticket is in its life (`statemachines/ticket.ts` says how it moves). */
export const TICKET_STATES = [
  'new',
  'open',
  'waiting_customer',
  'waiting_parts',
  'resolved',
] as const;
export type TicketState = (typeof TICKET_STATES)[number];

export const TICKET_STATE_LABEL: Record<TicketState, string> = {
  new: 'New',
  open: 'Open',
  waiting_customer: 'Waiting on customer',
  waiting_parts: 'Waiting on parts',
  resolved: 'Resolved',
};

/** What went wrong. */
export const TICKET_CATEGORIES = [
  'not_fixed',
  'damage',
  'part',
  'billing',
  'scheduling',
  'conduct',
  'other',
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const TICKET_CATEGORY_LABEL: Record<TicketCategory, string> = {
  not_fixed: 'Not fixed / came back',
  damage: 'Damage',
  part: 'Missed or wrong part',
  billing: 'Billing',
  scheduling: 'Scheduling / no-show',
  conduct: 'Conduct',
  other: 'Other',
};

export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

export const TICKET_PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/** Why it went wrong, recorded when the office resolves it ("what keeps going wrong"). */
export const TICKET_ROOT_CAUSES = [
  'workmanship',
  'part_failure',
  'wrong_diagnosis',
  'communication',
  'scheduling',
  'customer_caused',
  'billing_error',
  'other',
] as const;
export type TicketRootCause = (typeof TICKET_ROOT_CAUSES)[number];

export const TICKET_ROOT_CAUSE_LABEL: Record<TicketRootCause, string> = {
  workmanship: 'Workmanship',
  part_failure: 'Part failure',
  wrong_diagnosis: 'Wrong diagnosis',
  communication: 'Communication',
  scheduling: 'Scheduling',
  customer_caused: 'Customer-caused',
  billing_error: 'Billing error',
  other: 'Other',
};

/**
 * Who raised it: the office ("New ticket"), a tech ("Flag a problem" on a visit), or the
 * customer ("Something not right?" on their job report or invoice link, Phase 5b). A tech's
 * ticket always has a job, by a CHECK; a customer's always names the link it came through
 * (`TICKET_VIA_LINKS`), by another. `customer` was admitted by migration 0038.
 */
export const TICKET_SOURCES = ['office', 'tech', 'customer'] as const;
export type TicketSource = (typeof TICKET_SOURCES)[number];

/** Who raised it, as the Tickets page's Source filter and chip say it. */
export const TICKET_SOURCE_LABEL: Record<TicketSource, string> = {
  office: 'Office',
  tech: 'Tech',
  customer: 'Customer',
};

/**
 * The customer link a customer's ticket came through (`tickets.via_link`): the job report
 * (`/r/:token`) or the invoice (`/i/:token`). Only a customer's ticket has one.
 */
export const TICKET_VIA_LINKS = ['report', 'invoice'] as const;
export type TicketViaLink = (typeof TICKET_VIA_LINKS)[number];

/**
 * The four plain kinds of problem the customer's form offers (spec §3), in the form's order.
 * Each is filed under the ticket category of the same name; the office re-triages to the full
 * list (`TICKET_CATEGORIES`).
 */
export const CUSTOMER_PROBLEM_KINDS = [
  'not_fixed',
  'damage',
  'billing',
  'other',
] as const;
export type CustomerProblemKind = (typeof CUSTOMER_PROBLEM_KINDS)[number];

/** The category a customer's choice files the ticket under. */
export const CUSTOMER_PROBLEM_CATEGORY: Record<CustomerProblemKind, TicketCategory> = {
  not_fixed: 'not_fixed',
  damage: 'damage',
  billing: 'billing',
  other: 'other',
};

/** The words the customer chooses between (the page names the shop, never Servwell). */
export const CUSTOMER_PROBLEM_LABEL: Record<CustomerProblemKind, string> = {
  not_fixed: 'The problem came back or wasn’t fixed',
  damage: 'Something was damaged',
  billing: 'A question about the bill',
  other: 'Something else',
};

/**
 * The four kinds of problem a tech's "Flag a problem" asks for (ops insights Phase 6a spec §3,
 * R3), in the sheet's order. Each IS the ticket category of the same name (no mapping), so
 * these are a subset of `TICKET_CATEGORIES` and need no CHECK change. A flag that names none
 * (an offline flag queued before the kind existed) is filed as `TECH_FLAG_DEFAULT_KIND`.
 */
export const TECH_FLAG_KINDS = [
  'part',
  'damage',
  'not_fixed',
  'other',
] as const satisfies readonly TicketCategory[];
export type TechFlagKind = (typeof TECH_FLAG_KINDS)[number];

/** What a flag names when the tech does not choose: Other, so one tap still sends it. */
export const TECH_FLAG_DEFAULT_KIND: TechFlagKind = 'other';

/**
 * The tech's words for the four kinds (plain, not the office list's `TICKET_CATEGORY_LABEL`:
 * the office reads `part` as "Missed or wrong part").
 */
export const TECH_FLAG_KIND_LABEL: Record<TechFlagKind, string> = {
  part: 'Missing or wrong part',
  damage: 'Something damaged',
  not_fixed: 'Not fixed or came back',
  other: 'Other',
};

/**
 * How many tickets a customer link may have OPEN (not resolved) at once (spec Q3). A resolved
 * one frees its place.
 */
export const CUSTOMER_TICKET_OPEN_CAP = 3;

/**
 * Where an email of a customer-visible message stands (`ticket_emails.status`, Phase 5b). A
 * `failure` (`TICKET_EMAIL_FAILURES`) says why a `skipped` or `failed` one did not go.
 */
export const TICKET_EMAIL_STATUSES = [
  'queued',
  'sending',
  'sent',
  'skipped',
  'failed',
] as const;
export type TicketEmailStatus = (typeof TICKET_EMAIL_STATUSES)[number];

/**
 * Who may read a message. Every message is staff-visible; `customer` only marks what 5b may
 * send to the customer. A tech on the ticket sees both kinds.
 */
export const TICKET_MESSAGE_VISIBILITIES = ['internal', 'customer'] as const;
export type TicketMessageVisibility = (typeof TICKET_MESSAGE_VISIBILITIES)[number];

/**
 * Who wrote a message: the staff (always named) or the customer (from the form, Phase 5b, with
 * no author).
 */
export const TICKET_MESSAGE_AUTHOR_KINDS = ['staff', 'customer'] as const;
export type TicketMessageAuthorKind = (typeof TICKET_MESSAGE_AUTHOR_KINDS)[number];

/** The notifications about a ticket: each names its ticket (`notifications.ticket_id`). */
export const TICKET_NOTIFICATION_KINDS = [
  'ticket_assigned',
  'ticket_message',
  'ticket_raised',
] as const;
export type TicketNotificationKind = (typeof TICKET_NOTIFICATION_KINDS)[number];

/**
 * The notifications about an alert (ops insights Phase 7 spec §8): it was assigned to you, or it
 * escalated to you. Each names its alert (`notifications.alert_id`), never a ticket.
 */
export const ALERT_NOTIFICATION_KINDS = ['alert_assigned', 'alert_escalated'] as const;
export type AlertNotificationKind = (typeof ALERT_NOTIFICATION_KINDS)[number];

/** What a notification in the bell is about (CONTEXT.md "Notification"). */
export const NOTIFICATION_KINDS = [
  ...TICKET_NOTIFICATION_KINDS,
  ...ALERT_NOTIFICATION_KINDS,
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];
